import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { observer } from 'mobx-react';
import { Box, Breadcrumbs, Button, Grid, Typography } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { wizardStore } from '@/store/wizard.store';
import { SideMenu } from '@/components/wizard/SideMenu';
import { OptionCard } from '@/components/wizard/OptionCard';
import { Planimetry } from '@/components/wizard/Planimetry';
import { VirtualPreview } from '@/components/wizard/VirtualPreview';
import { PhaseTabBar } from '@/components/wizard/PhaseTabBar';
import { BaseConfigCard } from '@/components/wizard/BaseConfigCard';
import { SolutionsColumn } from '@/components/wizard/SolutionsColumn';
import { RelazioneLivingCucina } from '@/components/wizard/KitchenRelation';
import { SceltaLayoutScreen } from '@/components/wizard/SceltaLayoutScreen';
import { ConfiguratorIntro } from '@/components/wizard/ConfiguratorIntro';
import { PhaseTransition } from '@/components/wizard/PhaseTransition';
import { InvalidationDialog } from '@/components/wizard/InvalidationDialog';
import { LoadingState } from '@/components/common/LoadingState';
import { ErrorState } from '@/components/common/ErrorState';
import { baseOptions, baseOptionIds, hasVariantSelected } from '@/utils/options.utils';

const ConfiguratorPage = observer(function ConfiguratorPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const configurationId = id ? Number(id) : NaN;

  const [dialogOpen, setDialogOpen] = useState(false);
  const [pendingDeleteCount, setPendingDeleteCount] = useState(0);
  // Azione da eseguire alla conferma del dialog di invalidazione (pacchetto o opzioni).
  const pendingCommit = useRef<(() => void) | null>(null);

  useEffect(() => {
    if (Number.isNaN(configurationId)) return;
    wizardStore.reset();
    void wizardStore.enterConfigurator(configurationId);
  }, [configurationId]);

  if (Number.isNaN(configurationId)) {
    return <ErrorState message="Identificativo configurazione non valido." />;
  }

  const isInitialLoading = wizardStore.isLoadingMenu && wizardStore.menu.length === 0;

  const handleRetry = (): void => {
    void wizardStore.enterConfigurator(configurationId);
  };

  const handleSelectSubstep = (substepId: number, roomId: number | null): void => {
    void wizardStore.jumpToSubstep(substepId, roomId);
  };

  const handleSelectPhase = (phaseKey: string): void => {
    const phase = wizardStore.groupedMenu.find((p) => p.phaseKey === phaseKey);
    if (!phase) return;
    const first = phase.rooms
      .flatMap((r) => r.steps.flatMap((s) => s.substeps))
      .sort((a, b) => a.stepOrder - b.stepOrder || a.substepOrder - b.substepOrder)[0];
    if (first) void wizardStore.jumpToSubstep(first.substepId, first.roomId);
  };

  // ── Flusso a PACCHETTO (fasi 3-4): conferma → anteprima invalidazioni → salva + avanza ──
  const handleConfirmPackage = async (): Promise<void> => {
    const preview = await wizardStore.previewSelection();
    if (preview && preview.toDeleteIds.length > 0) {
      pendingCommit.current = () => void wizardStore.confirmSelection();
      setPendingDeleteCount(preview.toDeleteIds.length);
      setDialogOpen(true);
      return;
    }
    await wizardStore.confirmSelection();
  };

  // ── Flusso a OPZIONE (fasi 1-2): ogni toggle → anteprima invalidazioni → salva (resta sul substep) ──
  const handleApplyOptions = async (optionIds: number[]): Promise<void> => {
    if (optionIds.length === 0) return;
    const preview = await wizardStore.previewOptions(optionIds);
    if (preview && preview.toDeleteIds.length > 0) {
      pendingCommit.current = () => void wizardStore.saveOptions(optionIds);
      setPendingDeleteCount(preview.toDeleteIds.length);
      setDialogOpen(true);
      return;
    }
    await wizardStore.saveOptions(optionIds);
  };

  const handleDialogConfirm = (): void => {
    setDialogOpen(false);
    const commit = pendingCommit.current;
    pendingCommit.current = null;
    commit?.();
  };

  const handleDialogCancel = (): void => {
    setDialogOpen(false);
    pendingCommit.current = null;
  };

  const currentEntry = wizardStore.currentMenuEntry;
  const inputType = wizardStore.currentInputType;
  const isOptions = inputType === 'options';
  const isBusy =
    wizardStore.isSaving || wizardStore.isLoadingNext || wizardStore.isSavingOptions;
  const isLoadingContent = isOptions
    ? wizardStore.isLoadingOptions
    : wizardStore.isLoadingPackages;

  // PROSEGUI: per le opzioni basta che il substep sia risposto (le answer sono già salvate al toggle);
  // per i pacchetti serve una selezione da confermare.
  const canConfirm = isOptions
    ? currentEntry?.isAnswered === true
    : wizardStore.selectedPackageId !== null;
  const onConfirm = isOptions
    ? () => void wizardStore.proceed()
    : () => void handleConfirmPackage();

  const options = wizardStore.options;

  // Substep a griglia "pura" full-width, senza colonna sinistra (legacy ConfiguratorSelectionLayout):
  // MOOD (MoodGiornoNotte) e SCHEMA RIVESTIMENTO bagno (la planimetria LT_ del legacy sta solo nella
  // schermata intro `firstStep`, non nella vista card).
  const fullWidthSubstep =
    currentEntry?.substepKey === 'MOOD' || currentEntry?.substepKey === 'SCHEMARIVESTIMENTO';
  // Colonna sinistra: planimetria per le fasi a opzione e per la CARTA PARATI (overlay R_);
  // altrimenti l'immagine prodotto del pacchetto scelto.
  const leftUsesPlanimetry = isOptions || currentEntry?.substepKey === 'CARTAPARATI';
  const productPreview =
    wizardStore.selectedPackage?.virtualImageUrl ?? wizardStore.selectedPackage?.imageUrl ?? null;

  return (
    <Box sx={{ flex: 1, display: 'flex', alignItems: 'flex-start' }}>
      <Box sx={{ flex: 1, px: { xs: 2, md: 5 }, py: 4, minWidth: 0 }}>
        <Button
          variant="text"
          color="inherit"
          onClick={() => navigate('/configurations')}
          sx={{ mb: 2, pl: 0 }}
        >
          ← Le mie configurazioni
        </Button>

        <Typography variant="h2" gutterBottom>
          {wizardStore.metadata?.configurationName ?? `Configurazione #${configurationId}`}
        </Typography>

        {!isInitialLoading && !wizardStore.error && wizardStore.menu.length > 0 && (
          <PhaseTabBar
            phases={wizardStore.groupedMenu}
            currentPhaseKey={currentEntry?.phaseKey ?? null}
            onSelectPhase={handleSelectPhase}
          />
        )}

        {isInitialLoading && <LoadingState message="Caricamento del configuratore…" />}

        {wizardStore.error && (
          <ErrorState message={wizardStore.error} onRetry={handleRetry} />
        )}

        {!isInitialLoading && !wizardStore.error && wizardStore.isCompleted && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2,
              py: 8,
              textAlign: 'center',
            }}
          >
            <CheckCircleOutlineIcon sx={{ fontSize: 56, color: 'success.main' }} aria-hidden="true" />
            <Typography variant="h3">Configurazione completata</Typography>
            <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 480 }}>
              Hai risposto a tutti i passi disponibili. Puoi rivedere una scelta in qualsiasi
              momento selezionandola dal menu a destra.
            </Typography>
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={() => navigate(`/configurations/${configurationId}/resume`)}
              sx={{ mt: 1 }}
            >
              Vai al riepilogo
            </Button>
          </Box>
        )}

        {/* Config senza layout → primo step di LAYOUT: Scelta Layout (assegna il layoutId). */}
        {!isInitialLoading && !wizardStore.error && !wizardStore.isCompleted && wizardStore.menu.length === 0 && (
          <SceltaLayoutScreen
            configId={configurationId}
            onConfirmed={() => void wizardStore.enterConfigurator(configurationId)}
          />
        )}

        {/* Ingresso config con layout → schermata START/intro (elenco fasi) prima del wizard. */}
        {!isInitialLoading && !wizardStore.error && !wizardStore.isCompleted && wizardStore.menu.length > 0 && wizardStore.showIntro && (
          <ConfiguratorIntro configId={configurationId} />
        )}

        {/* Fase completata → schermata di transizione ("Passa alla fase successiva"). */}
        {!isInitialLoading && !wizardStore.error && !wizardStore.isCompleted && !wizardStore.showIntro && wizardStore.phaseTransition !== null && (
          <PhaseTransition />
        )}

        {!isInitialLoading && !wizardStore.error && !wizardStore.isCompleted && wizardStore.menu.length > 0 && !wizardStore.showIntro && wizardStore.phaseTransition === null && (
          <Box
            sx={{
              display: 'flex',
              gap: 3,
              alignItems: 'flex-start',
              flexDirection: { xs: 'column', md: 'row' },
            }}
          >
            {/* Colonna 1 — IL TUO APPARTAMENTO: planimetria + (opzioni) card base. Assente per il MOOD. */}
            {!fullWidthSubstep && (
            <Box
              sx={{
                flex: '0 0 auto',
                width: { xs: '100%', md: 360 },
                position: { md: 'sticky' },
                top: { md: 16 },
              }}
            >
              {/* Colonna sinistra: la PLANIMETRIA per le fasi a opzione e per i substep a pacchetto che
                  la usano (CARTA PARATI overlay R_, SCHEMA RIVESTIMENTO overlay LT_); altrimenti
                  l'immagine prodotto (render "Virtual" o copertina) del pacchetto selezionato. */}
              {leftUsesPlanimetry ? (
                <Planimetry
                  descriptor={wizardStore.planimetry}
                  isLoading={wizardStore.isLoadingPlanimetry}
                />
              ) : productPreview ? (
                <VirtualPreview
                  src={productPreview}
                  label={wizardStore.selectedPackage?.title ?? undefined}
                />
              ) : (
                <Planimetry
                  descriptor={wizardStore.planimetry}
                  isLoading={wizardStore.isLoadingPlanimetry}
                />
              )}
              {isOptions && !isLoadingContent && (
                <BaseConfigCard
                  baseOptions={baseOptions(options)}
                  confirmed={currentEntry?.isAnswered === true && !hasVariantSelected(options)}
                  answered={currentEntry?.isAnswered === true}
                  isBusy={isBusy}
                  onConfirm={() => void handleApplyOptions(baseOptionIds(options))}
                  phaseKey={currentEntry?.phaseKey ?? null}
                  configurationId={configurationId}
                />
              )}
            </Box>
            )}

            {/* Colonna 2 — contenuto: soluzioni aggiuntive (opzioni) o griglia pacchetti. */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              {currentEntry && (
                <Breadcrumbs sx={{ mb: 1 }} aria-label="Percorso nel configuratore">
                  <Typography variant="body2" color="text.secondary">
                    {currentEntry.phaseName}
                  </Typography>
                  {currentEntry.roomLabel && (
                    <Typography variant="body2" color="text.secondary">
                      {currentEntry.roomLabel}
                    </Typography>
                  )}
                  <Typography variant="body2" color="text.secondary">
                    {currentEntry.stepName}
                  </Typography>
                  <Typography variant="body2" color="text.primary" fontWeight={600}>
                    {currentEntry.substepName}
                  </Typography>
                </Breadcrumbs>
              )}

              {isLoadingContent ? (
                <LoadingState message="Caricamento delle opzioni…" />
              ) : isOptions ? (
                <SolutionsColumn
                  options={options}
                  copy={wizardStore.substepCopy}
                  substepKey={currentEntry?.substepKey ?? null}
                  isBusy={isBusy}
                  onApply={(optionIds) => void handleApplyOptions(optionIds)}
                />
              ) : (
                <Box>
                  {/* Titolo di sezione (fase BAGNO), es. "CONFIGURA IL PAVIMENTO DEL BAGNO". */}
                  {wizardStore.substepCopy?.heading && (
                    <Typography
                      variant="overline"
                      sx={{ color: 'text.primary', letterSpacing: '0.08em', fontWeight: 700, display: 'block', mb: 1 }}
                    >
                      {wizardStore.substepCopy.heading}
                    </Typography>
                  )}
                  {/* Domanda del substep (tagline) + nota, quando il BE le espone in `copy`. */}
                  {(wizardStore.substepCopy?.tagline || wizardStore.substepCopy?.note) && (
                    <Box sx={{ mb: 2 }}>
                      {wizardStore.substepCopy?.tagline && (
                        <Typography
                          sx={{
                            fontFamily: '"miller", serif',
                            fontStyle: 'italic',
                            fontSize: '1.125rem',
                            color: 'text.primary',
                            mb: wizardStore.substepCopy?.note ? 1 : 0,
                          }}
                        >
                          {wizardStore.substepCopy.tagline}
                        </Typography>
                      )}
                      {wizardStore.substepCopy?.note && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          {wizardStore.substepCopy.note}
                        </Typography>
                      )}
                    </Box>
                  )}
                  {currentEntry?.substepKey === 'RELAZIONELIVINGCUCINA' ? (
                    <RelazioneLivingCucina
                      packages={wizardStore.packages}
                      selectedPackageId={wizardStore.selectedPackageId}
                      onSelect={(packageId) => wizardStore.selectPackage(packageId)}
                    />
                  ) : (
                  <Grid container spacing={2} sx={{ mt: 1 }}>
                  {wizardStore.packages.map((pkg) => (
                    // MOOD: card più grandi/verticali (4 per riga); altri pacchetti: griglia 2-3 colonne.
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      lg={fullWidthSubstep ? 3 : 4}
                      key={pkg.packageId}
                    >
                      <OptionCard
                        pkg={pkg}
                        selected={wizardStore.selectedPackageId === pkg.packageId}
                        onSelect={(packageId) => wizardStore.selectPackage(packageId)}
                      />
                    </Grid>
                  ))}
                  {wizardStore.packages.length === 0 && (
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary">
                        Nessuna opzione disponibile per questo passo.
                      </Typography>
                    </Grid>
                  )}
                  </Grid>
                  )}
                </Box>
              )}
            </Box>
          </Box>
        )}
      </Box>

      {/* Colonna 3 — SideMenu (navbar accordion + PROSEGUI). */}
      {!isInitialLoading && !wizardStore.error && wizardStore.menu.length > 0 && (
        <SideMenu
          phases={wizardStore.groupedMenu}
          currentSubstepId={wizardStore.position?.substepId ?? null}
          currentRoomId={wizardStore.position?.roomId ?? null}
          onSelectSubstep={handleSelectSubstep}
          canConfirm={canConfirm}
          isBusy={isBusy}
          onConfirm={onConfirm}
        />
      )}

      <InvalidationDialog
        open={dialogOpen}
        affectedCount={pendingDeleteCount}
        onCancel={handleDialogCancel}
        onConfirm={handleDialogConfirm}
      />
    </Box>
  );
});

export default ConfiguratorPage;
